import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ImageUploadField from '../../components/admin/ImageUploadField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const empty = { title: '', excerpt: '', content: '', image_url: '', published: true };

const fmtDate = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });

export default function AdminNews() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchAll = async () => {
    try { const res = await axios.get(`${API}/news?published_only=false`); setArticles(res.data); }
    catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openAdd  = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, excerpt: a.excerpt, content: a.content, image_url: a.image_url, published: a.published });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.excerpt || !form.content || !form.image_url) { toast.error('Compila tutti i campi obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API}/news/${editing.id}`, form); toast.success('Articolo aggiornato!'); }
      else         { await axios.post(`${API}/news`, form); toast.success('Articolo pubblicato!'); }
      setModalOpen(false); fetchAll();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/news/${id}`); toast.success('Articolo eliminato'); setDeleteId(null); fetchAll(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const togglePublished = async (a) => {
    try { await axios.put(`${API}/news/${a.id}`, { published: !a.published }); toast.success(a.published ? 'Nascosto' : 'Pubblicato'); fetchAll(); }
    catch { toast.error('Errore'); }
  };

  return (
    <div className="min-w-0 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-xl font-bold text-gray-900">News</h2>
          <p className="font-body text-gray-500 text-xs mt-0.5">{articles.length} articoli totali</p>
        </div>
        <Button onClick={openAdd} className="bg-primary hover:bg-primary-dark text-white rounded-xl gap-2 flex-shrink-0">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nuovo Articolo</span>
          <span className="sm:hidden">Nuovo</span>
        </Button>
      </div>

      {/* Cards list — vertical only */}
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-gray-100" />)}</div>
      ) : articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 text-center py-16">
          <p className="font-body text-gray-400">Nessun articolo trovato</p>
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3 overflow-hidden">
              {/* Immagine */}
              <img
                src={a.image_url} alt={a.title}
                className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                onError={e => { e.target.style.display = 'none'; }}
              />
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-body font-semibold text-gray-900 text-sm truncate">{a.title}</p>
                <p className="font-body text-xs text-gray-400 truncate mt-0.5">{a.excerpt}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-body text-xs text-gray-400">{fmtDate(a.created_at)}</span>
                  <button
                    onClick={() => togglePublished(a)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${a.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {a.published ? <><Eye className="w-3 h-3" /> Pubb.</> : <><EyeOff className="w-3 h-3" /> Nascosto</>}
                  </button>
                </div>
              </div>
              {/* Azioni */}
              <div className="flex gap-0.5 flex-shrink-0">
                <button onClick={() => openEdit(a)} className="p-2 hover:bg-blue-50 rounded-lg text-blue-500 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => setDeleteId(a.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal — bottom sheet on mobile */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col rounded-t-2xl shadow-xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0">
              <h3 className="font-heading text-lg font-bold">{editing ? 'Modifica Articolo' : 'Nuovo Articolo'}</h3>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Titolo *</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titolo dell'articolo" className="rounded-xl" />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Anteprima *</Label>
                <Textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} rows={2} placeholder="Breve descrizione visibile nella lista" className="rounded-xl resize-none" />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Contenuto *</Label>
                <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={5} placeholder="Testo completo..." className="rounded-xl resize-none" />
              </div>
              <div>
                <Label className="font-body text-sm font-medium text-gray-700 mb-1.5 block">Immagine *</Label>
                <ImageUploadField value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} previewClass="h-24" />
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="published" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-primary" />
                <Label htmlFor="published" className="font-body text-sm text-gray-700 cursor-pointer">Pubblica subito</Label>
              </div>
            </div>
            <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 sm:rounded-b-2xl flex-shrink-0">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary-dark text-white rounded-xl">
                {saving ? 'Salvataggio...' : editing ? 'Aggiorna' : 'Pubblica'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold mb-2">Elimina Articolo</h3>
            <p className="font-body text-gray-500 text-sm mb-6">Sei sicuro? Questa azione è irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
